/**
 * Per-turn actions under a Work reply, backed by the workspace checkpoints
 * (one git commit per settled turn):
 *
 *   Restore    — put the workspace back to how it was after this turn. Only
 *                on earlier turns and only while the thread is idle; the next
 *                follow-up continues from the restored files.
 *
 * macOS project replies also show a request-scoped file summary after the run
 * settles, with links into saved-edit review and restoration.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, Pressable, ActivityIndicator, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useChatStore, useCoworkerStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { chatsApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import WorkProjectChangesCard from './WorkProjectChangesCard';
import {
  workCheckpointRestoreTarget,
  workIsActivelyRunning,
  workTurnActionMeta,
} from '@/constants/work';
import { isWorkFollowUpQueueMessage } from '@/constants/work_followup_queue';

function ActionLink({ icon, label, onPress, busy, disabled, colors, accessibilityLabel }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || busy}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || label}
      accessibilityState={{ busy: !!busy, disabled: !!(disabled || busy) }}
      hitSlop={Platform.OS === 'web' ? undefined : 8}
      style={({ hovered, pressed }) => [
        styles.action,
        (hovered || pressed) && !disabled && { opacity: 0.7 },
        disabled && styles.actionDisabled,
      ]}
    >
      {busy
        ? <ActivityIndicator size="small" color={colors.text_color_tertiary} style={styles.spinner} />
        : <Icon name={icon} size={11} color={colors.text_color_tertiary} />}
      <Text size="xxs" color="tertiary" weight="medium">{label}</Text>
    </Pressable>
  );
}

export default function WorkTurnActions({ taskId, conversationId, message }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore((s) => s.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const [busy, setBusy] = useState(null);

  const task = useCoworkerStore((s) => (taskId ? s.tasks[taskId] : null) || null);
  const events = useCoworkerStore((s) => (taskId ? s.eventsByTask[taskId] : null) || null);
  const checkpoints = useCoworkerStore((s) => (
    taskId && Object.prototype.hasOwnProperty.call(s.checkpointsByTask || {}, taskId)
      ? s.checkpointsByTask[taskId]
      : null
  ));
  const restoreCheckpoint = useCoworkerStore((s) => s.restoreCheckpoint);
  const fetchCheckpoints = useCoworkerStore((s) => s.fetchCheckpoints);

  const messages = useChatStore((s) => {
    const chat = s.chats.find((c) => c.id === conversationId);
    return chat?.messages || null;
  });
  const messageId = message?.id || message?._id;
  const turnMeta = useMemo(
    () => workTurnActionMeta(messages, messageId, { isQueuedMessage: isWorkFollowUpQueueMessage }),
    [messageId, messages],
  );
  const { turn, totalTurns, shouldRender } = turnMeta;

  useEffect(() => {
    if (taskId && checkpoints == null) void fetchCheckpoints(taskId);
  }, [checkpoints, fetchCheckpoints, taskId]);

  const isRunning = workIsActivelyRunning({ task, events });
  const restoreTarget = useMemo(
    () => workCheckpointRestoreTarget(checkpoints, turn, totalTurns),
    [checkpoints, totalTurns, turn],
  );

  const performRestore = useCallback(async () => {
    if (!taskId || !restoreTarget || busy) return;
    setBusy('restore');
    try {
      const restored = await restoreCheckpoint(taskId, restoreTarget);
      if (conversationId) {
        const conversation = await chatsApi.getConversationById(conversationId).catch(() => null);
        if (conversation) useChatStore.getState().addChats([conversation]);
      }
      addToast(
        restoreTarget.isUndo
          ? 'Restore undone — the latest workspace is back'
          : `Workspace restored to turn ${restoreTarget.turn} — your next message continues from there`,
        'success',
      );
      if (Array.isArray(restored?.warnings) && restored.warnings.length) {
        addToast(restored.warnings.join(' · '), 'warning');
      }
    } catch (err) {
      addToast(err?.message || 'Could not restore this turn', 'error');
    } finally {
      setBusy(null);
    }
  }, [addToast, busy, conversationId, restoreCheckpoint, restoreTarget, taskId]);

  const onRestore = useCallback(() => {
    if (!taskId || !restoreTarget || busy) return;
    showConfirmationModal({
      title: restoreTarget.isUndo ? 'Undo workspace restore?' : `Restore workspace to turn ${restoreTarget.turn}?`,
      message: restoreTarget.isUndo
        ? 'This replaces the current workspace files with the snapshot from immediately before the last restore.'
        : 'This replaces the current workspace files with this earlier snapshot. You can undo it from the latest turn.',
      confirmText: restoreTarget.isUndo ? 'Undo restore' : 'Restore workspace',
      isDangerous: true,
      onConfirm: performRestore,
    });
  }, [busy, performRestore, restoreTarget, showConfirmationModal, taskId]);

  const reviewRequest = Platform.OS === 'macos' && task?.localProject?.bindingId && message?.parameters?.workChangeSetId;
  if (!taskId || !shouldRender || isRunning || (!restoreTarget && !reviewRequest)) return null;

  return (
    <View>
      {reviewRequest && <WorkProjectChangesCard taskId={taskId} changeSetId={reviewRequest} />}
      {restoreTarget && <View style={styles.row}><ActionLink
        icon="clock-rotate-left"
        label={restoreTarget.isUndo ? 'Undo restore' : `Restore to turn ${restoreTarget.turn}`}
        accessibilityLabel={restoreTarget.isUndo
          ? 'Undo the last workspace restore'
          : `Restore the workspace to how it was after turn ${restoreTarget.turn}`}
        onPress={onRestore}
        busy={busy === 'restore'}
        disabled={busy !== null && busy !== 'restore'}
        colors={colors}
      /></View>}
    </View>
  );
}

WorkTurnActions.propTypes = {
  taskId: PropTypes.string,
  conversationId: PropTypes.string,
  message: PropTypes.object,
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 6,
    paddingHorizontal: 2,
  },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 2,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'opacity 0.15s ease' } }),
  },
  actionDisabled: {
    opacity: 0.45,
  },
  spinner: {
    transform: [{ scale: 0.6 }],
  },
});
